export interface ReportJsonFilter {
  [property: string]: any;
  key?: string;
  field?: string;
  operator?: string;
  value?: any;
  options?: string[] | string;
  and?: ReportJsonFilter[];
  or?: ReportJsonFilter[];
}
export type ReportFilterOperation = 'and' | 'or';

export function isReportFilterGroup(filter: ReportJsonFilter): boolean {
  return Array.isArray(filter?.and) || Array.isArray(filter?.or);
}

export function reportFilterOperation(filter: ReportJsonFilter): ReportFilterOperation {
  return Array.isArray(filter?.or) ? 'or' : 'and';
}

export function reportFilterChildren(filter: ReportJsonFilter): ReportJsonFilter[] {
  return filter[reportFilterOperation(filter)] || [];
}

export function changeReportFilterOperation(filter: ReportJsonFilter, operation: ReportFilterOperation): void {
  const children = reportFilterChildren(filter);
  delete filter.and;
  delete filter.or;
  filter[operation] = children;
}

function normalizeReportFilterOptions(filter: ReportJsonFilter): void {
  if (filter.options === undefined) return;
  const options = Array.isArray(filter.options) ? filter.options : filter.options ? [filter.options] : [];
  if (options.length) filter.options = options;
  else delete filter.options;
}

export function reportFiltersToJson(filters: ReportJsonFilter[]): ReportJsonFilter[] {
  return filters.map(filter => {
    if (isReportFilterGroup(filter)) {
      const operation = reportFilterOperation(filter);
      return {[operation]: reportFiltersToJson(reportFilterChildren(filter))};
    }
    const {key, ...jsonFilter} = filter;
    jsonFilter.field = jsonFilter.field ?? key;
    if (jsonFilter.operator === '=') jsonFilter.operator = '==';
    normalizeReportFilterOptions(jsonFilter);
    return jsonFilter;
  });
}

export function removeEmptyReportFilterGroups(filters: ReportJsonFilter[]): ReportJsonFilter[] {
  return filters.reduce<ReportJsonFilter[]>((cleaned, filter) => {
    if (isReportFilterGroup(filter)) {
      const operation = reportFilterOperation(filter);
      const children = removeEmptyReportFilterGroups(reportFilterChildren(filter));
      if (children.length) cleaned.push({[operation]: children});
    } else {
      cleaned.push(filter);
    }
    return cleaned;
  }, []);
}

export function hasValidReportJsonFilters(filters: ReportJsonFilter[]): boolean {
  return Array.isArray(filters) && filters.every(filter => {
    if (isReportFilterGroup(filter)) {
      const children = reportFilterChildren(filter);
      return children.length > 0 && hasValidReportJsonFilters(children);
    }
    return !!(filter?.key || filter?.field) && !!filter?.operator
      && filter.value !== undefined && filter.value !== null && filter.value !== '';
  });
}
